import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { authService } from "../../services";
import logo from "../../assets/pawsi.jpeg";

const BRAND = {
  olive: "#757B2B",
  coral: "#F3694D",
  ink: "#1E2218",
};

const strength = (value) => {
  const raw = String(value || "");
  let score = 0;
  if (raw.length >= 8) score += 30;
  if (/[A-Z]/.test(raw)) score += 20;
  if (/[a-z]/.test(raw)) score += 15;
  if (/\d/.test(raw)) score += 20;
  if (/[^A-Za-z0-9]/.test(raw)) score += 15;
  return Math.min(100, score);
};

function ForgotPasswordPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    username: "",
    email: "",
    document: "",
    new_password: "",
    new_password2: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  const passwordStrength = useMemo(() => strength(form.new_password), [form.new_password]);
  const canVerify = Boolean(form.username.trim() && (form.email.trim() || form.document.trim())) && !submitting;
  const canConfirm =
    canVerify &&
    form.new_password.length >= 8 &&
    form.new_password === form.new_password2 &&
    passwordStrength >= 65 &&
    !submitting;

  const update = (key) => (event) => setForm((prev) => ({ ...prev, [key]: event.target.value }));

  const handleVerify = async (event) => {
    event.preventDefault();
    if (!canVerify) return;
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      const response = await authService.verifyPasswordResetIdentity({
        username: form.username.trim(),
        email: form.email.trim(),
        document: form.document.trim(),
      });
      setMessage(response?.message || "Si los datos coinciden, podrás continuar.");
      if (response?.ok) {
        setStep(2);
      }
    } catch (err) {
      setError(err?.response?.data?.detail || "No se pudo procesar la solicitud.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async (event) => {
    event.preventDefault();
    if (!canConfirm) return;
    setSubmitting(true);
    setError("");
    setMessage("");
    try {
      const response = await authService.confirmPasswordReset({
        username: form.username.trim(),
        email: form.email.trim(),
        document: form.document.trim(),
        new_password: form.new_password,
        new_password2: form.new_password2,
      });
      navigate("/login", {
        replace: true,
        state: { message: response?.message || "Si los datos coinciden, la contraseña fue actualizada." },
      });
    } catch (err) {
      const data = err?.response?.data || {};
      setError(data?.new_password?.[0] || data?.new_password2?.[0] || data?.detail || "No se pudo cambiar la contraseña.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "grid",
        placeItems: "center",
        px: 2,
        background: `
          radial-gradient(900px 520px at 18% 12%, rgba(243,105,77,0.18), transparent 66%),
          radial-gradient(760px 480px at 82% 20%, rgba(117,123,43,0.13), transparent 70%),
          linear-gradient(180deg, #F5F7FA 0%, #EDF1F5 100%)
        `,
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          maxWidth: 460,
          borderRadius: 2,
          border: "1px solid rgba(30,34,24,0.10)",
          background: "rgba(255,255,255,0.95)",
          boxShadow: "0 18px 50px rgba(16,24,40,0.10)",
          overflow: "hidden",
        }}
      >
        <Box sx={{ height: 4, background: `linear-gradient(90deg, ${BRAND.olive}, ${BRAND.coral})` }} />
        <Stack spacing={2.2} sx={{ p: 4 }}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box component="img" src={logo} alt="Marca" sx={{ height: 58, width: "auto", objectFit: "contain" }} />
            <Box>
              <Typography sx={{ fontWeight: 900, color: BRAND.ink }}>Recuperar contraseña</Typography>
              <Typography sx={{ fontSize: 13.5, color: "text.secondary", mt: 0.4 }}>
                Verificación local sin envío de correo.
              </Typography>
            </Box>
          </Stack>

          {message ? <Alert severity="info" sx={{ borderRadius: 1.5 }}>{message}</Alert> : null}
          {error ? <Alert severity="error" sx={{ borderRadius: 1.5 }}>{error}</Alert> : null}

          <Stack component="form" spacing={1.7} onSubmit={step === 1 ? handleVerify : handleConfirm}>
            <TextField label="Usuario" value={form.username} onChange={update("username")} disabled={submitting || step === 2} fullWidth />
            <TextField label="Correo registrado" value={form.email} onChange={update("email")} disabled={submitting || step === 2} fullWidth />
            <TextField label="DPI / documento" value={form.document} onChange={update("document")} disabled={submitting || step === 2} fullWidth />

            {step === 2 ? (
              <>
                <TextField label="Nueva contraseña" type="password" value={form.new_password} onChange={update("new_password")} disabled={submitting} fullWidth />
                <Box>
                  <LinearProgress variant="determinate" value={passwordStrength} sx={{ height: 7, borderRadius: 99 }} />
                  <Typography variant="caption" color="text.secondary">
                    Usa 8+ caracteres, mayúsculas, minúsculas, números y símbolo.
                  </Typography>
                </Box>
                <TextField
                  label="Confirmar contraseña"
                  type="password"
                  value={form.new_password2}
                  onChange={update("new_password2")}
                  error={Boolean(form.new_password2 && form.new_password !== form.new_password2)}
                  helperText={form.new_password2 && form.new_password !== form.new_password2 ? "Las contraseñas no coinciden." : " "}
                  disabled={submitting}
                  fullWidth
                />
              </>
            ) : null}

            <Button type="submit" variant="contained" disabled={step === 1 ? !canVerify : !canConfirm} sx={{ py: 1.1, fontWeight: 800 }}>
              {step === 1 ? "Verificar datos" : "Cambiar contraseña"}
            </Button>
            <Button type="button" onClick={() => navigate("/login")} disabled={submitting} sx={{ textTransform: "none" }}>
              Volver al inicio de sesión
            </Button>
          </Stack>
        </Stack>
      </Paper>
    </Box>
  );
}

export default ForgotPasswordPage;
